import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { apiGet, apiPost } from '../../utils/api';
import { useUrlState } from '../../hooks/useUrlState';
import './SeverancePanel.css';

// 퇴직정산 › 일반퇴직금.
//   퇴직자 정산: 퇴사일이 있는 직원의 실제 퇴직금·퇴직소득세·지급 방식
//   퇴직금 시뮬레이션: 재직자가 고른 날 퇴사하면 얼마인지, 앞으로 달마다 어떻게 늘어나는지
// 계산은 모두 서버(/api/payroll/companies/{id}/severance)가 한다. 화면은 넣고 뺄 항목·칸만 고른다.
// 퇴사일은 마지막 근무일이고, 법상 퇴직일은 그 다음 날이다.

const API = '/api/payroll';
const MODE_LABEL = { include: '3개월 임금', annual: '1년 치 × 3/12', exclude: '빼기' };
const NEXT_MODE = { include: 'annual', annual: 'exclude', exclude: 'include' };
const LINE_AMOUNT = '#2a78d6';
const LINE_NET = '#eb6834';

const won = (v) => `${Math.round(v || 0).toLocaleString('ko-KR')}원`;
const num = (v) => Math.round(v || 0).toLocaleString('ko-KR');
const manwon = (v) => `${Math.round((v || 0) / 10000).toLocaleString('ko-KR')}만`;
const endOfMonth = () => { const d = new Date(); const e = new Date(d.getFullYear(), d.getMonth() + 1, 0); return `${e.getFullYear()}-${String(e.getMonth() + 1).padStart(2, '0')}-${String(e.getDate()).padStart(2, '0')}`; };
const tenure = (days) => { const y = Math.floor(days / 365); const m = Math.floor((days % 365) / 30.4); return `${y ? `${y}년 ` : ''}${m}개월`; };

function SimTip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="sev-tip">
      <b>{p.last_work} 퇴사</b>
      {!p.eligible ? <div>재직 1년 미만 — 퇴직금 없음</div> : (
        <>
          <div><i style={{ background: LINE_AMOUNT }} />퇴직금 <span>{won(p.amount)}</span></div>
          <div><i style={{ background: LINE_NET }} />{p.irp ? 'IRP 이전액' : '세후 지급액'} <span>{won(p.net)}</span></div>
          {!p.irp && <div className="muted">퇴직소득세·지방소득세 {won(p.tax)}</div>}
        </>
      )}
      <div className="muted">누르면 이 날로 계산합니다</div>
    </div>
  );
}

function SeverancePanel({ company }) {
  const [url, setUrl] = useUrlState();
  // 주소에 두는 상태: 화면(정산/시뮬레이션), 직원, 가정 퇴사일, 1일 소정근로시간, 인상률, 기간
  const mode = url.get('sev_mode') === 'simulate' ? 'simulate' : 'settle';
  const employeeId = url.get('sev');
  const lastWork = url.get('sev_last') || '';
  const hours = Number(url.get('sev_hours')) || 8;
  const raise = Number(url.get('sev_raise')) || 0;
  const horizon = Number(url.get('sev_horizon')) || 24;

  const [people, setPeople] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [itemModes, setItemModes] = useState({});
  const [cut, setCut] = useState([]);
  const [exceptions, setExceptions] = useState([]);

  useEffect(() => {
    let alive = true;
    setPeople(null);
    apiGet(`${API}/companies/${company.id}/employees`).then((r) => { if (alive) setPeople(r.employees); }).catch((e) => alive && setError(e.message));
    return () => { alive = false; };
  }, [company.id]);

  // 직원·화면이 바뀌면 사람이 고른 조정은 처음으로
  useEffect(() => { setItemModes({}); setCut([]); setExceptions([]); }, [employeeId, mode]);

  const list = useMemo(() => {
    if (!people) return [];
    if (mode === 'settle') {
      return people.filter((p) => p.leave_date && p.last_sheet === 'fulltime').sort((a, b) => (a.leave_date < b.leave_date ? 1 : -1));
    }
    return people.filter((p) => p.status === '재직' && p.last_sheet === 'fulltime');
  }, [people, mode]);

  const request = useMemo(() => JSON.stringify({
    employee_id: employeeId, last_work: lastWork || (mode === 'simulate' ? endOfMonth() : null),
    item_modes: itemModes, excluded_cells: cut, daily_hours: hours, exceptions,
    simulate: mode === 'simulate', horizon, raise_rate: raise,
  }), [employeeId, lastWork, mode, itemModes, cut, hours, exceptions, horizon, raise]);

  useEffect(() => {
    if (!employeeId) { setResult(null); return undefined; }
    let alive = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const r = await apiPost(`${API}/companies/${company.id}/severance`, JSON.parse(request));
        if (alive) { setResult(r); setError(''); }
      } catch (e) {
        if (alive) { setResult(null); setError(e.message); }
      } finally {
        if (alive) setLoading(false);
      }
    }, 200);
    return () => { alive = false; clearTimeout(timer); };
  }, [company.id, request, employeeId]);

  const pick = (id) => setUrl({ sev: id, sev_last: null });
  const toggleCut = (key) => setCut((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const cycleMode = (label, current) => setItemModes((prev) => ({ ...prev, [label]: NEXT_MODE[current] }));
  const toggleException = (key) => setExceptions((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const r = result;
  const firstEligible = r && r.series ? r.series.find((s) => s.eligible) : null;

  return (
    <div className="sev">
      <aside className="sev-side">
        <div className="sev-seg">
          <button type="button" className={mode === 'settle' ? 'on' : ''} onClick={() => setUrl({ sev_mode: null, sev: null, sev_last: null })}>퇴직자 정산</button>
          <button type="button" className={mode === 'simulate' ? 'on' : ''} onClick={() => setUrl({ sev_mode: 'simulate', sev: null, sev_last: null })}>퇴직금 시뮬레이션</button>
        </div>
        <p className="sev-muted">{mode === 'settle' ? '퇴사일이 있는 상용직, 최근 퇴사 순' : '재직 중인 상용직 — 퇴사일을 바꿔 가며 예상해 봅니다'}</p>
        <ul className="sev-people">
          {!people && <li className="sev-muted">불러오는 중…</li>}
          {people && list.length === 0 && <li className="sev-muted">{mode === 'settle' ? '퇴사일이 있는 직원이 없습니다.' : '재직 중인 상용직이 없습니다.'}</li>}
          {list.map((p) => (
            <li key={p.employee_id}>
              <button type="button" className={employeeId === p.employee_id ? 'on' : ''} onClick={() => pick(p.employee_id)}>
                <b>{p.name}</b>
                <span>{mode === 'settle' ? `퇴사 ${p.leave_date}` : `입사 ${p.hire_date || '–'}`}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="sev-main">
        {!employeeId && <div className="sev-empty">왼쪽에서 직원을 고르세요.</div>}
        {employeeId && error && <div className="sev-error">{error}</div>}
        {employeeId && !r && !error && <div className="sev-empty">계산 중…</div>}
        {r && (
          <div className={loading ? 'sev-body is-loading' : 'sev-body'}>
            <header className="sev-head">
              <div>
                <h3>{r.name} <span className="sev-muted">{mode === 'simulate' ? '퇴직금 시뮬레이션' : '퇴직자 정산'}</span></h3>
                <div className="sev-dates">
                  입사 <b>{r.hire_date}</b> · 퇴사일(마지막 근무일) <b>{r.last_work}</b> → 퇴직일 <b>{r.retire_day}</b> · 재직 {num(r.service_days)}일({tenure(r.service_days)})
                  {r.projected && <span className="sev-tag">{r.latest_ym} 이후는 예상(최근 달 지급이 이어진다고 가정)</span>}
                </div>
              </div>
              {mode === 'simulate' && (
                <div className="sev-controls">
                  <label>퇴사일(마지막 근무일)<input type="date" value={lastWork || endOfMonth()} onChange={(e) => setUrl({ sev_last: e.target.value }, { replace: true })} /></label>
                  <label>연 인상률(%)<input type="number" step="0.5" value={raise} onChange={(e) => setUrl({ sev_raise: e.target.value === '0' ? null : e.target.value }, { replace: true })} /></label>
                  <label>그래프 기간
                    <select value={horizon} onChange={(e) => setUrl({ sev_horizon: e.target.value === '24' ? null : e.target.value }, { replace: true })}>
                      {[12, 24, 36, 60].map((h) => <option key={h} value={h}>{h}개월</option>)}
                    </select>
                  </label>
                </div>
              )}
            </header>

            <div className="sev-cards">
              <div className="sev-card"><span>퇴직금</span><b>{r.eligible ? won(r.amount) : '없음'}</b><em>{r.eligible ? `1일 ${r.used} ${won(r.daily_wage)} × 30 × ${num(r.service_days)} ÷ 365` : '재직 1년 미만'}</em></div>
              <div className="sev-card"><span>퇴직소득세 + 지방소득세</span><b>{won((r.payment.income_tax || 0) + (r.payment.local_tax || 0))}</b><em>{r.payment.irp ? '이연(IRP) — 지금 떼지 않음' : r.tax && r.tax.small_exempt ? '1천원 미만 소액부징수' : `소득세 ${won(r.payment.income_tax)} · 지방 ${won(r.payment.local_tax)}`}</em></div>
              <div className="sev-card strong"><span>{r.payment.irp ? 'IRP 이전액' : '실지급액'}</span><b>{won(r.payment.net)}</b><em>{r.payment.irp ? '세금은 근로자가 IRP 에서 찾을 때' : `퇴직금 − 원천징수 ${won(r.payment.withheld)}`}</em></div>
              <div className={`sev-card pay ${r.payment.irp ? 'irp' : 'cash'}`}><span>지급 방식</span><b>{r.amount <= 0 ? '–' : r.payment.irp ? 'IRP 이전' : '일반 계좌'}</b><em>{r.payment.reason}</em></div>
            </div>

            {mode === 'simulate' && r.series && (
              <section className="sev-block">
                <h4>퇴사일에 따른 예상 <span className="sev-muted">그달 말일에 퇴사한다고 보면 · 점을 누르면 그 날로 계산</span></h4>
                <div className="sev-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={r.series} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                      onClick={(e) => { if (e && e.activePayload) setUrl({ sev_last: e.activePayload[0].payload.last_work }, { replace: true }); }}>
                      <CartesianGrid stroke="#eef0f2" vertical={false} />
                      <XAxis dataKey="last_work" tickFormatter={(v) => `${v.slice(2, 4)}.${v.slice(5, 7)}`} tick={{ fontSize: 11, fill: '#6c757d' }} tickLine={false} minTickGap={16} />
                      <YAxis tickFormatter={manwon} tick={{ fontSize: 11, fill: '#6c757d' }} tickLine={false} axisLine={false} width={56} />
                      <Tooltip content={<SimTip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      <ReferenceLine y={3000000} stroke="#adb5bd" strokeDasharray="4 4" label={{ value: 'IRP 기준 300만원', position: 'insideTopLeft', fontSize: 11, fill: '#6c757d' }} />
                      {firstEligible && <ReferenceLine x={firstEligible.last_work} stroke="#1baf7a" strokeDasharray="4 4" label={{ value: '퇴직금 발생', position: 'insideTopRight', fontSize: 11, fill: '#1b7a55' }} />}
                      <ReferenceLine x={r.series.find((s) => s.last_work >= r.last_work)?.last_work} stroke="#16191d" />
                      <Line type="monotone" dataKey="amount" name="퇴직금" stroke={LINE_AMOUNT} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="net" name="실지급액(IRP 이전액)" stroke={LINE_NET} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            <section className="sev-block">
              <h4>평균임금 <span className="sev-muted">산정 기간 {r.period.start} ~ {r.period.end} · {r.period.raw_days}일{r.period.excluded_days ? ` − 휴직·수습 ${r.period.excluded_days}일` : ''} = {r.period.days}일</span></h4>
              <p className="sev-hint">항목 이름을 누르면 넣는 방식이 바뀝니다(3개월 임금 → 1년 치 × 3/12 → 빼기). 금액 칸을 누르면 그 칸만 뺍니다.</p>
              <div className="sev-table-wrap">
                <table className="sev-table">
                  <thead>
                    <tr>
                      <th>달</th>
                      {r.items.map((it) => (
                        <th key={it.label}>
                          <button type="button" className={`sev-mode mode-${it.mode}`} onClick={() => cycleMode(it.label, it.mode)} title="눌러서 넣는 방식 바꾸기">
                            {it.label}<em>{MODE_LABEL[it.mode]}</em>
                          </button>
                        </th>
                      ))}
                      <th className="num">평균임금에 넣은 금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.rows.map((row) => (
                      <tr key={row.ym} className={`${row.in_period ? '' : 'outside'} ${row.projected ? 'projected' : ''}`}>
                        <th>
                          {row.ym}{row.projected && <span className="sev-tag">예상</span>}
                          <small>{row.in_period ? `${row.overlap_days}/${row.month_days}일` : '1년 치 항목만'}</small>
                        </th>
                        {r.items.map((it) => {
                          const cell = row.cells.find((c) => c.label === it.label);
                          if (!cell) return <td key={it.label} className="empty">–</td>;
                          const key = `${row.ym}|${it.label}`;
                          return (
                            <td key={it.label} className={`num ${cell.cut ? 'cut' : ''} mode-${cell.mode}`}>
                              <button type="button" onClick={() => toggleCut(key)} title={cell.cut ? '다시 넣기' : '이 칸 빼기'}>
                                {num(cell.amount)}
                                {cell.mode === 'include' && !cell.cut && row.in_period && Math.abs(cell.used - cell.amount) > 0.5 && <small>→ {num(cell.used)}</small>}
                              </button>
                            </td>
                          );
                        })}
                        <td className="num total">{row.in_period ? num(row.used) : '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <dl className="sev-calc">
                <dt>3개월 임금</dt><dd>{won(r.wage3)}</dd>
                <dt>1년 치 항목 × 3/12</dt><dd>{won(r.annual_total)} × 3/12 = {won(r.annual_add)}{r.annual.length > 0 && <span className="sev-muted"> ({r.annual.map((a) => a.label).join(', ')})</span>}</dd>
                <dt>1일 평균임금</dt><dd>({won(r.wage3)} + {won(r.annual_add)}) ÷ {r.period.days}일 = <b>{won(r.average_daily)}</b></dd>
                <dt>1일 통상임금</dt>
                <dd>
                  통상시급 {won(r.hourly)} ×
                  <input className="sev-hours" type="number" step="0.5" min="1" value={hours} onChange={(e) => setUrl({ sev_hours: e.target.value === '8' ? null : e.target.value }, { replace: true })} />
                  시간 = <b>{won(r.ordinary_daily)}</b>
                </dd>
                <dt>적용</dt><dd><b>{r.used}</b> {won(r.daily_wage)} <span className="sev-muted">(평균임금이 통상임금보다 적으면 통상임금)</span></dd>
                <dt>퇴직금</dt><dd>{won(r.daily_wage)} × 30 × {num(r.service_days)}일 ÷ 365 = <b>{won(r.amount)}</b>{!r.eligible && <span className="sev-warn"> 재직 1년 미만 — 퇴직금 없음</span>}</dd>
              </dl>
            </section>

            {r.tax && (
              <section className="sev-block">
                <h4>퇴직소득세 <span className="sev-muted">{r.tax.rule} 규정</span></h4>
                <dl className="sev-calc">
                  <dt>근속연수</dt><dd>{r.tax.years}년 <span className="sev-muted">(1년 미만 끝수는 1년)</span></dd>
                  <dt>근속연수공제</dt><dd>{won(r.tax.service_deduction)}</dd>
                  <dt>환산급여</dt><dd>({won(r.amount)} − {won(r.tax.service_deduction)}) × 12 ÷ {r.tax.years} = {won(r.tax.converted)}</dd>
                  <dt>환산급여공제</dt><dd>{won(r.tax.converted_deduction)}</dd>
                  <dt>과세표준</dt><dd>{won(r.tax.base)}</dd>
                  <dt>환산산출세액</dt><dd>{won(r.tax.converted_tax)}</dd>
                  <dt>산출세액</dt><dd>{won(r.tax.converted_tax)} × {r.tax.years} ÷ 12 = {won(r.tax.computed)}</dd>
                  <dt>퇴직소득세</dt><dd><b>{won(r.tax.income_tax)}</b>{r.tax.small_exempt && <span className="sev-warn"> 1천원 미만이라 걷지 않음(소액부징수)</span>}</dd>
                  <dt>지방소득세</dt><dd><b>{won(r.tax.local_tax)}</b> <span className="sev-muted">(소득세의 10%, 10원 미만 버림)</span></dd>
                </dl>
              </section>
            )}

            {r.amount > 0 && (
              <section className="sev-block">
                <h4>지급 방식</h4>
                <p>{r.payment.reason}</p>
                <div className="sev-exceptions">
                  <span className="sev-muted">IRP 예외(자동 판정: 만 55세 이상 · 외국인{r.age != null ? ` — 퇴직일 만 ${r.age}세` : ''})</span>
                  <label><input type="checkbox" checked={exceptions.includes('death')} onChange={() => toggleException('death')} /> 사망</label>
                  <label><input type="checkbox" checked={exceptions.includes('loan')} onChange={() => toggleException('loan')} /> 담보대출 상환</label>
                </div>
              </section>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default SeverancePanel;
